import React, { useState, useEffect } from 'react';
import { tenantsAPI, statsAPI, mpAPI } from '../lib/api';

const Dashboard = () => {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [mpConfig, setMpConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, statsRes, mpRes] = await Promise.all([
        tenantsAPI.list(),
        statsAPI.get(),
        mpAPI.getConfig().catch(() => ({ data: { configured: false } }))
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
      setMpConfig(mpRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (tenant) => {
    const status = tenant.subscription_status;
    const plan = tenant.subscription_plan;
    
    const styles = {
      active: 'bg-green-100 text-green-700 border-green-200',
      trialing: 'bg-blue-100 text-blue-700 border-blue-200',
      pending_payment: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      past_due: 'bg-amber-100 text-amber-700 border-amber-200',
      suspended: 'bg-orange-100 text-orange-700 border-orange-200',
      canceled: 'bg-red-100 text-red-700 border-red-200',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    };
    const labels = {
      active: plan === 'lifetime' ? 'Vitalicio' : 'Pago',
      trialing: 'Trial',
      pending_payment: 'Aguard. Pagamento',
      past_due: 'Inadimplente',
      suspended: 'Suspenso',
      canceled: 'Cancelado',
      pending: 'Pendente',
    };
    return (
      <span data-testid={`status-badge-${tenant.id}`} className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPlanBadge = (tenant) => {
    const plan = tenant.subscription_plan;
    const managed = tenant.subscription_managed_by;
    
    const planLabels = {
      monthly: 'Mensal',
      yearly: 'Anual',
      lifetime: 'Vitalicio',
    };
    
    const planStyles = {
      monthly: 'bg-indigo-50 text-indigo-600',
      yearly: 'bg-purple-50 text-purple-600',
      lifetime: 'bg-emerald-50 text-emerald-700',
    };

    return (
      <div className="flex items-center gap-1.5">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${planStyles[plan] || 'bg-gray-50 text-gray-600'}`}>
          {planLabels[plan] || plan || '-'}
        </span>
        {managed === 'mercadopago' && (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-sky-50 text-sky-600 font-medium">MP</span>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return 0;
    try {
      const end = new Date(dateStr);
      const now = new Date();
      return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    } catch {
      return 0;
    }
  };

  const filteredTenants = tenants.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'lifetime') return t.subscription_plan === 'lifetime';
    return t.subscription_status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="Total Clientes" value={stats.total_tenants} color="gray" />
          <StatCard label="Ativos" value={stats.active} color="green" />
          <StatCard label="Em Trial" value={stats.trialing} color="blue" />
          <StatCard label="Inadimplentes" value={stats.past_due} color="amber" />
          <StatCard label="Vitalicios" value={stats.lifetime} color="emerald" />
          <StatCard label="MRR" value={formatCurrency(stats.mrr)} color="emerald" isCurrency />
          <StatCard label="ARR" value={formatCurrency(stats.arr)} color="emerald" isCurrency />
        </div>
      )}

      {/* MP Status */}
      {mpConfig && (
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${mpConfig.configured ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'}`}>
          <div className={`w-2 h-2 rounded-full ${mpConfig.configured ? 'bg-sky-500' : 'bg-amber-500'}`} />
          Mercado Pago: {mpConfig.configured ? `Configurado (${mpConfig.environment})` : 'Nao configurado'}
          {mpConfig.configured && (
            <span className="ml-2 text-xs opacity-75">
              Mensal: {formatCurrency(mpConfig.valor_mensal)} | Anual: {formatCurrency(mpConfig.valor_anual)}
            </span>
          )}
        </div>
      )}

      {/* Filter & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { key: 'all', label: 'Todos' },
            { key: 'active', label: 'Ativos' },
            { key: 'trialing', label: 'Trial' },
            { key: 'past_due', label: 'Inadimp.' },
            { key: 'suspended', label: 'Suspensos' },
            { key: 'lifetime', label: 'Vitalicios' },
            { key: 'canceled', label: 'Cancelados' },
          ].map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          data-testid="btn-novo-cliente"
          onClick={() => setShowNewModal(true)}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Cliente
        </button>
      </div>

      {/* Tenants Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Dominio</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Plano</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Dias Rest.</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Criado em</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Gerenciar</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Nenhum cliente encontrado
                  </td>
                </tr>
              ) : (
                filteredTenants.map((tenant) => {
                  const days = getDaysRemaining(tenant.current_period_end || tenant.trial_ends_at);
                  const isLifetime = tenant.subscription_plan === 'lifetime';
                  return (
                    <tr key={tenant.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedTenant(tenant)}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{tenant.nome}</p>
                          <p className="text-xs text-gray-500">{tenant.email_financeiro || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-blue-600">{tenant.dominio || '-'}</span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(tenant)}</td>
                      <td className="px-4 py-3">
                        {tenant.manutencao ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium border bg-amber-100 text-amber-700 border-amber-200">Manutenção</span>
                        ) : (
                          getPlanBadge(tenant)
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isLifetime ? (
                          <span className="text-sm font-medium text-emerald-600">Infinito</span>
                        ) : (
                          <span className={`text-sm font-medium ${days <= 7 ? 'text-red-600' : 'text-gray-900'}`}>
                            {days} dias
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(tenant.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`btn-gerenciar-${tenant.id}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedTenant(tenant); }}
                          className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700"
                        >
                          Gerenciar
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showNewModal && (
        <NewTenantModal onClose={() => setShowNewModal(false)} onSuccess={() => { setShowNewModal(false); loadData(); }} />
      )}
      {selectedTenant && (
        <TenantManageModal
          tenant={selectedTenant}
          mpConfig={mpConfig}
          onClose={() => setSelectedTenant(null)}
          onRefresh={() => { setSelectedTenant(null); loadData(); }}
        />
      )}
    </div>
  );
};

// ========== STAT CARD ==========
const StatCard = ({ label, value, color, isCurrency }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`${isCurrency ? 'text-lg' : 'text-2xl'} font-bold text-${color}-600`}>{value}</p>
  </div>
);

// ========== STATUS INLINE BADGE (header do modal) ==========
const getStatusInline = (tenant) => {
  const status = tenant.subscription_status;
  const plan = tenant.subscription_plan;
  const styles = {
    active: 'bg-green-100 text-green-700',
    trialing: 'bg-blue-100 text-blue-700',
    pending_payment: 'bg-yellow-100 text-yellow-700',
    past_due: 'bg-amber-100 text-amber-700',
    suspended: 'bg-orange-100 text-orange-700',
    canceled: 'bg-red-100 text-red-700',
  };
  const labels = {
    active: plan === 'lifetime' ? 'Vitalício' : 'Pago',
    trialing: 'Trial',
    pending_payment: 'Aguard. Pagamento',
    past_due: 'Inadimplente',
    suspended: 'Suspenso',
    canceled: 'Cancelado',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {labels[status] || status}
    </span>
  );
};

// ========== TENANT MANAGE MODAL ==========
const TenantManageModal = ({ tenant, mpConfig, onClose, onRefresh }) => {
  const [tab, setTab] = useState('assinatura');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [mpStatus, setMpStatus] = useState(null);
  const [mpForm, setMpForm] = useState({ plano: 'monthly', payer_email: tenant.email_financeiro || '' });
  const [manutencaoMsg, setManutencaoMsg] = useState(tenant.manutencao_mensagem || 'Estamos realizando melhorias no sistema. Voltaremos em breve.');
  const [mensalidadeForm, setMensalidadeForm] = useState({
    valor_mensal: tenant.valor_mensal ?? mpConfig?.valor_mensal ?? '',
    valor_anual: tenant.valor_anual ?? mpConfig?.valor_anual ?? '',
    dia_vencimento: tenant.dia_vencimento ?? '',
    dias_suspensao: tenant.dias_suspensao ?? 5,
    taxa_instalacao: tenant.taxa_instalacao ?? '',
    motivo: '',
  });
  const [indicacaoForm, setIndicacaoForm] = useState({
    indicado_por_nome: tenant.indicado_por_nome || '',
    indicado_por_contato: tenant.indicado_por_contato || '',
    indicacao_observacao: tenant.indicacao_observacao || '',
  });
  // Aba "Pagamentos": histórico, pagamento manual, estender trial
  const [pagHistorico, setPagHistorico] = useState({ pagamentos: [], trial_extensoes: [] });
  const [pagManualForm, setPagManualForm] = useState({ metodo: 'pix', observacao: '' });
  const [trialForm, setTrialForm] = useState({ dias: 7, motivo: '' });

  // Aba "Módulos Extras"
  const [modulosForm, setModulosForm] = useState({
    financeiro_ativo: !!tenant?.modulos_extras?.financeiro?.ativo,
    financeiro_valor: tenant?.modulos_extras?.financeiro?.valor ?? 100,
    whatsapp_ativo: !!tenant?.modulos_extras?.whatsapp?.ativo,
    whatsapp_valor: tenant?.modulos_extras?.whatsapp?.valor ?? 80,
  });

  // Breakdown da cobrança (plano + add-ons)
  const [cobrancaDetalhe, setCobrancaDetalhe] = useState(null);
  const loadCobrancaDetalhe = async () => {
    try {
      const plano = tenant?.subscription_plan === 'yearly' ? 'yearly' : 'monthly';
      const res = await tenantsAPI.getCobrancaDetalhe(tenant.id, plano);
      setCobrancaDetalhe(res.data);
    } catch (e) {
      console.error('Erro ao carregar detalhe de cobrança:', e);
    }
  };

  const handleSaveModuloFinanceiro = async () => {
    try {
      setLoading(true);
      const valor = parseFloat(modulosForm.financeiro_valor);
      if (isNaN(valor) || valor < 0) {
        setMessage({ type: 'error', text: 'Valor inválido' });
        setLoading(false);
        return;
      }
      await tenantsAPI.toggleModuloFinanceiro(tenant.id, {
        ativo: modulosForm.financeiro_ativo,
        valor,
      });
      setMessage({ type: 'success', text: `Módulo Financeiro ${modulosForm.financeiro_ativo ? 'ativado' : 'desativado'}` });
      await loadCobrancaDetalhe();
      onRefresh && onRefresh();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erro ao salvar' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveModuloWhatsapp = async () => {
    try {
      setLoading(true);
      const valor = parseFloat(modulosForm.whatsapp_valor);
      if (isNaN(valor) || valor < 0) {
        setMessage({ type: 'error', text: 'Valor inválido' });
        setLoading(false);
        return;
      }
      await tenantsAPI.toggleModuloWhatsapp(tenant.id, {
        ativo: modulosForm.whatsapp_ativo,
        valor,
      });
      setMessage({ type: 'success', text: `Módulo Atendimento WhatsApp ${modulosForm.whatsapp_ativo ? 'ativado' : 'desativado'}` });
      await loadCobrancaDetalhe();
      onRefresh && onRefresh();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erro ao salvar' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'historico') loadHistorico();
    if (tab === 'mercadopago' && tenant.mp_subscription_id) loadMpStatus();
    if (tab === 'pagamentos') loadHistoricoPagamentos();
    if (tab === 'mensalidade' || tab === 'modulos') loadCobrancaDetalhe();
  }, [tab]);

  const loadHistoricoPagamentos = async () => {
    try {
      const res = await tenantsAPI.historicoPagamentos(tenant.id);
      setPagHistorico(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAtivarPlanoAuto = async (plano) => {
    if (!window.confirm(
      plano === 'lifetime'
        ? 'Ativar plano VITALÍCIO? Não haverá cobranças.'
        : `Ativar plano ${plano === 'monthly' ? 'Mensal' : 'Anual'}?\n\nO link MP será gerado e enviado por e-mail automaticamente. Status: aguardando 1º pagamento.`
    )) return;
    setLoading(true);
    try {
      const res = await tenantsAPI.ativarPlano(tenant.id, { plano, enviar_email: true });
      const enviado = res?.data?.email_enviado;
      if (plano === 'lifetime') {
        showMsg('Plano vitalício ativado!');
      } else {
        showMsg(enviado ? `Plano ${plano} ativado. Link enviado por e-mail!` : `Plano ${plano} ativado.`);
      }
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao ativar plano', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePagamentoManual = async () => {
    if (!window.confirm(`Confirmar pagamento manual?\nMétodo: ${pagManualForm.metodo}\n\nO próximo vencimento será empurrado para o próximo dia configurado.`)) return;
    setLoading(true);
    try {
      const res = await tenantsAPI.pagamentoManual(tenant.id, pagManualForm);
      showMsg(`Pagamento registrado! Novo vencimento: ${res.data.current_period_end?.slice(0, 10)}`);
      setPagManualForm({ metodo: 'pix', observacao: '' });
      loadHistoricoPagamentos();
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao registrar pagamento', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReconciliarMP = async () => {
    if (!window.confirm(`Buscar no Mercado Pago pagamentos aprovados dos últimos 90 dias e creditar os que ainda não foram lançados?\n\nEsta operação é IDEMPOTENTE (não credita duas vezes o mesmo pagamento).`)) return;
    setLoading(true);
    try {
      const res = await tenantsAPI.mpReconciliarAgora(tenant.id);
      const data = res.data || {};
      const nCreditados = (data.creditados || []).length;
      const nExistentes = data.ja_existentes_count || 0;
      const nErros = (data.erros || []).length;
      if (nCreditados > 0) {
        const totalCreditado = data.creditados.reduce((s, c) => s + (c.valor || 0), 0);
        showMsg(`✅ ${nCreditados} pagamento(s) creditado(s)! Total: R$ ${totalCreditado.toFixed(2)}`);
      } else if (nExistentes > 0) {
        showMsg(`Nada novo. Todos os ${nExistentes} pagamento(s) do MP já estavam creditados no sistema.`);
      } else if (nErros > 0) {
        showMsg(`Erro ao consultar MP: ${JSON.stringify(data.erros[0])}`, 'error');
      } else {
        showMsg('Nenhum pagamento aprovado encontrado no MP nos últimos 90 dias para este cliente.');
      }
      loadHistoricoPagamentos();
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao reconciliar Mercado Pago', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEstenderTrial = async () => {
    if (!trialForm.dias || !trialForm.motivo.trim()) {
      showMsg('Informe dias e motivo', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await tenantsAPI.estenderTrial(tenant.id, { dias: parseInt(trialForm.dias, 10), motivo: trialForm.motivo.trim() });
      showMsg(`Trial estendido para ${res.data.trial_ends_at?.slice(0, 10)}`);
      setTrialForm({ dias: 7, motivo: '' });
      loadHistoricoPagamentos();
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao estender trial', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorico = async () => {
    try {
      const res = await tenantsAPI.getHistorico(tenant.id);
      setHistorico(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMpStatus = async () => {
    try {
      const res = await tenantsAPI.mpVerificar(tenant.id);
      setMpStatus(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleManualAction = async (acao, extra = {}) => {
    if (acao === 'cancelar' && !window.confirm('Tem certeza que deseja cancelar esta assinatura?')) return;
    if (acao === 'suspender' && !window.confirm('Tem certeza que deseja suspender o acesso?')) return;
    
    setLoading(true);
    try {
      const payload = { acao, ...extra };
      await tenantsAPI.gerenciarAssinatura(tenant.id, payload);
      showMsg(`Acao '${acao}' executada com sucesso!`);
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao executar acao', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMpCriar = async () => {
    if (!mpForm.payer_email) {
      showMsg('Informe o e-mail do pagador', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await tenantsAPI.mpCriarAssinatura(tenant.id, mpForm);
      const enviado = res?.data?.email_enviado;
      showMsg(enviado ? 'Assinatura criada! Link enviado por e-mail.' : 'Assinatura criada! Link gerado.');
      setMpStatus(res.data);
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao criar assinatura no MP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarTaxaInstalacao = async () => {
    if (!window.confirm('Gerar link de cobrança da Taxa de Instalação?\nO link será enviado por e-mail para o e-mail financeiro do tenant.')) return;
    setLoading(true);
    try {
      const res = await tenantsAPI.mpGerarTaxaInstalacao(tenant.id, { enviar_email: true });
      const enviado = res?.data?.email_enviado;
      showMsg(enviado ? 'Taxa gerada! Link enviado por e-mail.' : 'Taxa gerada! Link disponível abaixo.');
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao gerar taxa', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReenviarLinkAssinatura = async () => {
    setLoading(true);
    try {
      const res = await tenantsAPI.mpReenviarLinkAssinatura(tenant.id);
      const enviado = res?.data?.email_enviado;
      const dest = res?.data?.payer_email || '';
      showMsg(enviado ? `Link enviado por e-mail para ${dest}` : 'Falha ao enviar e-mail (verifique Resend)', enviado ? 'success' : 'error');
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao reenviar link', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRegerarLinkAssinatura = async () => {
    const plano = tenant.subscription_plan === 'yearly' ? 'yearly' : 'monthly';
    const payer_email = tenant.mp_payer_email || tenant.email_financeiro || '';
    if (!payer_email) {
      showMsg('Tenant não tem e-mail do pagador cadastrado', 'error');
      return;
    }
    if (!window.confirm(
      `Regerar link da assinatura ${plano === 'yearly' ? 'Anual' : 'Mensal'}?\n` +
      `Valor atual: R$ ${plano === 'yearly' ? (tenant.valor_anual ?? 0) : (tenant.valor_mensal ?? 0)}\n` +
      `O link antigo será substituído.`
    )) return;
    setLoading(true);
    try {
      const res = await tenantsAPI.mpCriarAssinatura(tenant.id, { plano, payer_email, enviar_email: true });
      const enviado = res?.data?.email_enviado;
      showMsg(enviado ? `Novo link gerado e enviado para ${payer_email}` : 'Novo link gerado!');
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao regerar link', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMpCancelar = async () => {
    if (!window.confirm('Cancelar assinatura no Mercado Pago?')) return;
    setLoading(true);
    try {
      await tenantsAPI.mpCancelar(tenant.id);
      showMsg('Assinatura cancelada no Mercado Pago');
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao cancelar', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleManutencao = async (ativar) => {
    const confirmMsg = ativar
      ? `ATIVAR manutenção para "${tenant.nome}"? Os usuários não conseguirão acessar.`
      : `DESATIVAR manutenção para "${tenant.nome}"? O sistema voltará ao normal.`;
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    try {
      await tenantsAPI.toggleManutencao(tenant.id, { ativar, mensagem: manutencaoMsg });
      showMsg(ativar ? 'Manutenção ativada!' : 'Manutenção desativada!');
      setTimeout(onRefresh, 1500);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao alterar manutenção', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMensalidade = async () => {
    const parseVal = (v) => parseFloat(String(v).replace(',', '.'));
    setLoading(true);
    try {
      const payload = { motivo: mensalidadeForm.motivo };
      if (mensalidadeForm.valor_mensal !== '' && mensalidadeForm.valor_mensal !== null) {
        const v = parseVal(mensalidadeForm.valor_mensal);
        if (!isNaN(v)) payload.valor_mensal = v;
      }
      if (mensalidadeForm.valor_anual !== '' && mensalidadeForm.valor_anual !== null) {
        const v = parseVal(mensalidadeForm.valor_anual);
        if (!isNaN(v)) payload.valor_anual = v;
      }
      if (mensalidadeForm.dia_vencimento !== '' && mensalidadeForm.dia_vencimento !== null) {
        payload.dia_vencimento = parseInt(mensalidadeForm.dia_vencimento, 10);
      }
      if (mensalidadeForm.dias_suspensao !== '' && mensalidadeForm.dias_suspensao !== null) {
        payload.dias_suspensao = parseInt(mensalidadeForm.dias_suspensao, 10);
      }
      if (mensalidadeForm.taxa_instalacao !== '' && mensalidadeForm.taxa_instalacao !== null) {
        const v = parseVal(mensalidadeForm.taxa_instalacao);
        if (!isNaN(v)) payload.taxa_instalacao = v;
      }
      await tenantsAPI.alterarMensalidade(tenant.id, payload);
      showMsg('Mensalidade atualizada!');
      setTimeout(onRefresh, 1200);
    } catch (e) {
      const detalhe =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === 'string' ? e.response.data : null) ||
        e?.message ||
        'Erro ao alterar';
      showMsg(`Erro: ${detalhe}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIndicacao = async () => {
    setLoading(true);
    try {
      await tenantsAPI.atualizarIndicacao(tenant.id, indicacaoForm);
      showMsg('Indicação atualizada!');
      setTimeout(onRefresh, 1200);
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Erro ao salvar', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('pt-BR'); } catch { return '-'; }
  };

  const tabs = [
    { key: 'assinatura', label: 'Dados Gerais', icon: '👤', group: 'Perfil' },
    { key: 'indicacao', label: 'Indicação', icon: '🎯', group: 'Perfil' },
    { key: 'mensalidade', label: 'Plano & Valores', icon: '💰', group: 'Financeiro' },
    { key: 'modulos', label: 'Módulos Extras', icon: '🧩', group: 'Financeiro' },
    { key: 'mercadopago', label: 'Mercado Pago', icon: '💳', group: 'Financeiro' },
    { key: 'pagamentos', label: 'Ações & Histórico', icon: '⚡', group: 'Financeiro' },
    { key: 'manutencao', label: 'Manutenção', icon: '🔧', group: 'Sistema' },
    { key: 'historico', label: 'Log de Eventos', icon: '📊', group: 'Sistema' },
  ];

  const grupos = ['Perfil', 'Financeiro', 'Sistema'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl shadow-xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0 bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
              {(tenant.nome || '?').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">{tenant.nome}</h2>
                {getStatusInline(tenant)}
              </div>
              <p className="text-xs text-gray-500">
                {tenant.dominio || tenant.slug} · DB: {tenant.db_name} · {tenant.email_financeiro || 'sem e-mail'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="btn-close-modal">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-6 mt-3 px-4 py-2 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message.text}
          </div>
        )}

        {/* Body: sidebar + content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <aside className="w-60 shrink-0 border-r bg-gray-50 overflow-y-auto py-3">
            {grupos.map(g => (
              <div key={g} className="mb-2">
                <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                  {g}
                </div>
                {tabs.filter(t => t.group === g).map(t => (
                  <button
                    key={t.key}
                    data-testid={`tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                      tab === t.key
                        ? 'bg-emerald-600 text-white font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-base leading-none">{t.icon}</span>
                    <span className="truncate">{t.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </aside>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
          {/* TAB: Assinatura (Manual) */}
          {tab === 'assinatura' && (
            <div className="space-y-5">
              {/* Status atual */}
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Status" value={tenant.subscription_status} />
                <InfoRow label="Plano" value={tenant.subscription_plan || '-'} />
                <InfoRow label="Gerenciado por" value={tenant.subscription_managed_by === 'mercadopago' ? 'Mercado Pago' : 'Manual'} />
                <InfoRow label="Valido ate" value={tenant.subscription_plan === 'lifetime' ? 'Infinito' : formatDate(tenant.current_period_end)} />
              </div>

              <hr />

              {/* Ações manuais */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Acoes Manuais</h3>
                <div className="grid grid-cols-2 gap-2">
                  <ActionButton
                    data-testid="btn-ativar-mensal"
                    label="Ativar Mensal"
                    color="green"
                    disabled={loading}
                    onClick={() => handleManualAction('ativar', { plano: 'monthly' })}
                  />
                  <ActionButton
                    data-testid="btn-ativar-anual"
                    label="Ativar Anual"
                    color="green"
                    disabled={loading}
                    onClick={() => handleManualAction('ativar', { plano: 'yearly' })}
                  />
                  <ActionButton
                    data-testid="btn-vitalicia"
                    label="Assinatura Vitalicia"
                    color="emerald"
                    disabled={loading}
                    onClick={() => handleManualAction('vitalicia')}
                  />
                  <ActionButton
                    data-testid="btn-estender-30"
                    label="Estender +30 dias"
                    color="blue"
                    disabled={loading}
                    onClick={() => handleManualAction('estender', { dias: 30 })}
                  />
                  <ActionButton
                    data-testid="btn-suspender"
                    label="Suspender Acesso"
                    color="orange"
                    disabled={loading}
                    onClick={() => handleManualAction('suspender')}
                  />
                  <ActionButton
                    data-testid="btn-cancelar"
                    label="Cancelar Assinatura"
                    color="red"
                    disabled={loading}
                    onClick={() => handleManualAction('cancelar')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB: Manutenção */}
          {tab === 'mensalidade' && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                <p className="text-sm text-blue-900">
                  Defina valores específicos para este tenant. Deixe em branco para usar o padrão global
                  (Mensal: R$ {(mpConfig?.valor_mensal ?? 0).toFixed(2)} · Anual: R$ {(mpConfig?.valor_anual ?? 0).toFixed(2)}).
                </p>
              </div>

              {/* Breakdown de cobrança com add-ons */}
              {cobrancaDetalhe && (
                <div className="p-4 rounded-lg border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3">
                    💰 Valor real cobrado na próxima mensalidade
                  </p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between py-1">
                      <span className="text-gray-700">{cobrancaDetalhe.base_label}</span>
                      <span className="font-medium">R$ {cobrancaDetalhe.base.toFixed(2)}</span>
                    </div>
                    {cobrancaDetalhe.addons.map(a => (
                      <div key={a.chave} className="flex justify-between py-1 text-cyan-700">
                        <span>▸ {a.label}</span>
                        <span className="font-medium">+ R$ {a.valor.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-emerald-300 mt-2">
                      <span className="font-bold text-emerald-800">TOTAL</span>
                      <span className="text-xl font-bold text-emerald-700">R$ {cobrancaDetalhe.valor_total.toFixed(2)}</span>
                    </div>
                  </div>
                  {!cobrancaDetalhe.tem_addons && (
                    <p className="text-xs text-gray-500 mt-2 italic">
                      Nenhum add-on ativo. Ative módulos extras na aba "Módulos Extras" para somar ao valor.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mensal (R$)</label>
                  <input
                    type="text" inputMode="decimal"
                    className="w-full px-3 py-2 border rounded-md"
                    value={mensalidadeForm.valor_mensal}
                    onChange={(e) => setMensalidadeForm(f => ({ ...f, valor_mensal: e.target.value }))}
                    data-testid="input-valor-mensal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Anual (R$)</label>
                  <input
                    type="text" inputMode="decimal"
                    className="w-full px-3 py-2 border rounded-md"
                    value={mensalidadeForm.valor_anual}
                    onChange={(e) => setMensalidadeForm(f => ({ ...f, valor_anual: e.target.value }))}
                    data-testid="input-valor-anual"
                  />
                </div>
              </div>

              <div className="p-3 rounded-lg border bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-900 mb-3 font-medium">
                  Cobrança Recorrente (Mercado Pago) — opcional
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dia de Vencimento (1-28)</label>
                    <input
                      type="number" min="1" max="28"
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Ex: 10"
                      value={mensalidadeForm.dia_vencimento}
                      onChange={(e) => setMensalidadeForm(f => ({ ...f, dia_vencimento: e.target.value }))}
                      data-testid="input-dia-vencimento"
                    />
                    <p className="text-xs text-gray-500 mt-1">Dia fixo do mês para cobrança</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dias para Suspensão</label>
                    <input
                      type="number" min="0" max="90"
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Ex: 5"
                      value={mensalidadeForm.dias_suspensao}
                      onChange={(e) => setMensalidadeForm(f => ({ ...f, dias_suspensao: e.target.value }))}
                      data-testid="input-dias-suspensao"
                    />
                    <p className="text-xs text-gray-500 mt-1">Carência após vencimento</p>
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                <p className="text-xs text-blue-900 mb-3 font-medium">
                  Taxa de Instalação — opcional (cobrança única)
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor da Taxa de Instalação (R$)</label>
                  <input
                    type="text" inputMode="decimal"
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Ex: 500,00"
                    value={mensalidadeForm.taxa_instalacao}
                    onChange={(e) => setMensalidadeForm(f => ({ ...f, taxa_instalacao: e.target.value }))}
                    data-testid="input-taxa-instalacao"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Salve o valor aqui e depois gere o link na aba "Mercado Pago".
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da alteração (opcional)</label>
                <input
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Ex: desconto fidelidade, promoção, etc."
                  value={mensalidadeForm.motivo}
                  onChange={(e) => setMensalidadeForm(f => ({ ...f, motivo: e.target.value }))}
                  data-testid="input-motivo-mensalidade"
                />
              </div>
              <button
                onClick={handleSaveMensalidade}
                disabled={loading}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="save-mensalidade-btn"
              >
                {loading ? 'Salvando...' : 'Salvar Mensalidade'}
              </button>
            </div>
          )}

          {tab === 'indicacao' && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
                <p className="text-sm text-purple-900">
                  Registre quem indicou este tenant. Usado para compor o relatório de indicações.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Indicador *</label>
                <input
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Ex: João da Silva"
                  value={indicacaoForm.indicado_por_nome}
                  onChange={(e) => setIndicacaoForm(f => ({ ...f, indicado_por_nome: e.target.value }))}
                  data-testid="input-indicado-por-nome"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contato (telefone, e-mail)</label>
                <input
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Ex: +55 67 99999-0000 ou joao@email.com"
                  value={indicacaoForm.indicado_por_contato}
                  onChange={(e) => setIndicacaoForm(f => ({ ...f, indicado_por_contato: e.target.value }))}
                  data-testid="input-indicado-por-contato"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Detalhes adicionais sobre a indicação"
                  value={indicacaoForm.indicacao_observacao}
                  onChange={(e) => setIndicacaoForm(f => ({ ...f, indicacao_observacao: e.target.value }))}
                  data-testid="input-indicacao-obs"
                />
              </div>
              <button
                onClick={handleSaveIndicacao}
                disabled={loading}
                className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                data-testid="save-indicacao-btn"
              >
                {loading ? 'Salvando...' : 'Salvar Indicação'}
              </button>
            </div>
          )}

          {tab === 'modulos' && (
            <div className="space-y-5" data-testid="tab-modulos-content">
              <div className="bg-gradient-to-r from-cyan-50 to-emerald-50 border border-cyan-200 rounded-lg p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-2xl shrink-0">💰</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">Módulo Financeiro</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Sistema completo de gestão financeira (serviços, despesas, parcelas, recibos PDF e dashboard).
                      Compartilha a base de clientes do CRM. Add-on cobrado separadamente do plano principal.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${modulosForm.financeiro_ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                        {modulosForm.financeiro_ativo ? '● Ativo' : '○ Desativado'}
                      </span>
                      {tenant?.modulos_extras?.financeiro?.ativado_em && (
                        <span className="text-xs text-gray-500">
                          ativado em {new Date(tenant.modulos_extras.financeiro.ativado_em).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status do Módulo</label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modulosForm.financeiro_ativo}
                      onChange={(e) => setModulosForm(f => ({ ...f, financeiro_ativo: e.target.checked }))}
                      className="w-5 h-5 rounded text-cyan-600 focus:ring-cyan-500"
                      data-testid="toggle-modulo-financeiro"
                    />
                    <span className="text-sm font-medium">
                      {modulosForm.financeiro_ativo ? 'Módulo Financeiro ATIVADO' : 'Módulo Financeiro DESATIVADO'}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Quando desativado, os usuários da empresa não conseguem acessar /financeiro.
                  </p>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor Mensal do Add-on (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={modulosForm.financeiro_valor}
                    onChange={(e) => setModulosForm(f => ({ ...f, financeiro_valor: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    data-testid="input-valor-modulo-financeiro"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Valor cobrado por mês pelo uso do módulo. Configurável por tenant.
                  </p>
                </div>
              </div>

              <button
                onClick={handleSaveModuloFinanceiro}
                disabled={loading}
                className="w-full py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 font-medium"
                data-testid="btn-salvar-modulo-financeiro"
              >
                {loading ? 'Salvando...' : 'Salvar Configuração'}
              </button>

              {/* Breakdown ao vivo */}
              {cobrancaDetalhe && (
                <div className="p-4 rounded-lg border-2 border-cyan-200 bg-cyan-50">
                  <p className="text-xs font-semibold text-cyan-800 uppercase tracking-wider mb-3">
                    📊 Impacto na próxima cobrança
                  </p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span>{cobrancaDetalhe.base_label}</span>
                      <span className="font-medium">R$ {cobrancaDetalhe.base.toFixed(2)}</span>
                    </div>
                    {cobrancaDetalhe.addons.map(a => (
                      <div key={a.chave} className="flex justify-between text-cyan-700">
                        <span>▸ {a.label}</span>
                        <span className="font-medium">+ R$ {a.valor.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-cyan-300 mt-2">
                      <span className="font-bold">TOTAL MENSAL</span>
                      <span className="text-xl font-bold text-cyan-700">R$ {cobrancaDetalhe.valor_total.toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-600 mt-2">
                    💡 Este é o valor que será cobrado automaticamente nos próximos PIX/cartão gerados pelo sistema (manual ou cron de aviso).
                  </p>
                </div>
              )}

              {/* ============ WHATSAPP CARD ============ */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5 mt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-2xl shrink-0">💬</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">Atendimento WhatsApp</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Inbox, Contatos, Templates, Chatbot conectado via QR Code. Cada usuário conecta seu WhatsApp Web ao sistema.
                      Add-on cobrado separadamente do plano principal.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${modulosForm.whatsapp_ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                        {modulosForm.whatsapp_ativo ? '● Ativo' : '○ Desativado'}
                      </span>
                      {tenant?.modulos_extras?.whatsapp?.ativado_em && (
                        <span className="text-xs text-gray-500">
                          ativado em {new Date(tenant.modulos_extras.whatsapp.ativado_em).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status do Módulo</label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modulosForm.whatsapp_ativo}
                      onChange={(e) => setModulosForm(f => ({ ...f, whatsapp_ativo: e.target.checked }))}
                      className="w-5 h-5 rounded text-green-600 focus:ring-green-500"
                      data-testid="toggle-modulo-whatsapp"
                    />
                    <span className="text-sm font-medium">
                      {modulosForm.whatsapp_ativo ? 'Atendimento WhatsApp ATIVADO' : 'Atendimento WhatsApp DESATIVADO'}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Quando desativado, o menu "Atendimento" some do sistema e /api/whatsapp/* retorna 403.
                  </p>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor Mensal do Add-on (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={modulosForm.whatsapp_valor}
                    onChange={(e) => setModulosForm(f => ({ ...f, whatsapp_valor: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="input-valor-modulo-whatsapp"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Valor cobrado por mês pelo uso do módulo. Configurável por tenant.
                  </p>
                </div>
              </div>

              <button
                onClick={handleSaveModuloWhatsapp}
                disabled={loading}
                className="w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                data-testid="btn-salvar-modulo-whatsapp"
              >
                {loading ? 'Salvando...' : 'Salvar Configuração'}
              </button>
            </div>
          )}

          {tab === 'manutencao' && (
            <div className="space-y-5">
              <div className={`p-4 rounded-lg border ${tenant.manutencao ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${tenant.manutencao ? 'bg-amber-500 animate-pulse' : 'bg-green-500'}`} />
                  <p className={`font-medium ${tenant.manutencao ? 'text-amber-800' : 'text-green-800'}`}>
                    {tenant.manutencao ? 'Manutenção ATIVA — Usuários não conseguem acessar' : 'Sistema operando normalmente'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem exibida para os usuários</label>
                <textarea
                  data-testid="manutencao-msg-input"
                  value={manutencaoMsg}
                  onChange={(e) => setManutencaoMsg(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Estamos realizando melhorias no sistema..."
                />
              </div>

              <div className="flex gap-3">
                {tenant.manutencao ? (
                  <button
                    data-testid="btn-desativar-manutencao"
                    onClick={() => handleToggleManutencao(false)}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Desativando...' : 'Desativar Manutenção'}
                  </button>
                ) : (
                  <button
                    data-testid="btn-ativar-manutencao"
                    onClick={() => handleToggleManutencao(true)}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Ativando...' : 'Ativar Manutenção'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* TAB: Mercado Pago */}
          {tab === 'mercadopago' && (
            <div className="space-y-5">
              {!mpConfig?.configured ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                  <p className="font-medium">Mercado Pago nao configurado</p>
                  <p className="mt-1">Configure as variaveis <code>MP_ACCESS_TOKEN</code> e <code>MP_PUBLIC_KEY</code> no arquivo <code>.env</code> do admin backend.</p>
                </div>
              ) : (
                <>
                  {/* Bloco unificado: link de cobrança ativa (se houver) */}
                  {tenant.mp_pix_mensal_init_point && (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sky-800">Link de Cobrança Ativa (PIX / Cartão / Boleto)</h4>
                        {(() => {
                          const plano = tenant.subscription_plan === 'yearly' ? 'yearly' : 'monthly';
                          const valor = plano === 'yearly' ? tenant.valor_anual : tenant.valor_mensal;
                          if (valor == null) return null;
                          return (
                            <span
                              data-testid="badge-valor-assinatura"
                              className="text-xs bg-sky-200 text-sky-800 px-2 py-1 rounded-full font-medium"
                            >
                              R$ {Number(valor).toFixed(2)}{plano === 'monthly' ? '/mês' : '/ano'}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <InfoRow label="E-mail" value={tenant.mp_payer_email || tenant.email_financeiro || '-'} small />
                        <InfoRow label="Ciclo" value={tenant.mp_pix_mensal_period || '-'} small />
                      </div>
                      <div className="pt-2">
                        <label className="text-xs text-gray-500 block mb-1">Link de Pagamento (PIX + Cartão + Boleto):</label>
                        <div className="flex gap-2">
                          <input
                            data-testid="mp-link-input"
                            type="text"
                            readOnly
                            value={tenant.mp_pix_mensal_init_point}
                            className="flex-1 text-xs px-3 py-2 border rounded-lg bg-white"
                          />
                          <button
                            data-testid="btn-copiar-link"
                            onClick={() => { navigator.clipboard.writeText(tenant.mp_pix_mensal_init_point); showMsg('Link copiado!'); }}
                            className="px-3 py-2 bg-sky-600 text-white rounded-lg text-xs hover:bg-sky-700"
                          >
                            Copiar
                          </button>
                          <button
                            data-testid="btn-enviar-link-email"
                            onClick={handleReenviarLinkAssinatura}
                            disabled={loading}
                            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 disabled:opacity-50"
                            title="Reenviar este link por e-mail"
                          >
                            Enviar por E-mail
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Bloco: Taxa de Instalação (Pagamento Único) */}
              <div className="mt-4 p-4 rounded-lg border bg-indigo-50 border-indigo-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-indigo-800">Taxa de Instalação (cobrança única)</h4>
                  {tenant.taxa_instalacao > 0 && (
                    <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-1 rounded-full">
                      R$ {Number(tenant.taxa_instalacao).toFixed(2)}
                    </span>
                  )}
                </div>

                {!tenant.taxa_instalacao || tenant.taxa_instalacao <= 0 ? (
                  <p className="text-xs text-indigo-700">
                    Configure o valor da taxa em "Mensalidade → Taxa de Instalação" antes de gerar a cobrança.
                  </p>
                ) : (
                  <>
                    {tenant.mp_taxa_instalacao_init_point && (
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Link da Taxa:</label>
                        <div className="flex gap-2">
                          <input
                            data-testid="taxa-link-input"
                            type="text"
                            readOnly
                            value={tenant.mp_taxa_instalacao_init_point}
                            className="flex-1 text-xs px-3 py-2 border rounded-lg bg-white"
                          />
                          <button
                            data-testid="btn-copiar-taxa-link"
                            onClick={() => { navigator.clipboard.writeText(tenant.mp_taxa_instalacao_init_point); showMsg('Link copiado!'); }}
                            className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700"
                          >
                            Copiar
                          </button>
                          <button
                            data-testid="btn-reenviar-taxa-email"
                            onClick={handleGerarTaxaInstalacao}
                            disabled={loading}
                            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 disabled:opacity-50"
                            title="Gera novo link e reenvia por e-mail"
                          >
                            Reenviar E-mail
                          </button>
                        </div>
                      </div>
                    )}
                    <button
                      data-testid="btn-gerar-taxa"
                      onClick={handleGerarTaxaInstalacao}
                      disabled={loading}
                      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {loading ? 'Gerando...' : (tenant.mp_taxa_instalacao_init_point ? 'Gerar novo link e reenviar e-mail' : 'Gerar Link da Taxa e Enviar por E-mail')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* TAB: Pagamentos / Histórico */}
          {tab === 'pagamentos' && (
            <div className="space-y-5">

              {/* Bloco: Ativar Plano Automatizado */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-emerald-800">⚡ Ativar Plano (PIX + Cartão + Boleto)</h4>
                <p className="text-xs text-emerald-700">
                  Define o plano e envia automaticamente por e-mail um link <strong>único</strong> que aceita PIX, Cartão de Crédito ou Boleto via Mercado Pago.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    data-testid="btn-ativar-monthly"
                    onClick={() => handleAtivarPlanoAuto('monthly')}
                    disabled={loading}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50 font-medium"
                  >
                    Mensal
                  </button>
                  <button
                    data-testid="btn-ativar-yearly"
                    onClick={() => handleAtivarPlanoAuto('yearly')}
                    disabled={loading}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50 font-medium"
                  >
                    Anual
                  </button>
                  <button
                    data-testid="btn-ativar-lifetime"
                    onClick={() => handleAtivarPlanoAuto('lifetime')}
                    disabled={loading}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 font-medium"
                  >
                    Vitalício
                  </button>
                </div>
              </div>

              {/* Bloco: Cobrança PIX deste mês */}
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sky-800">💲 Gerar Cobrança PIX (mensalidade atual)</h4>
                <p className="text-xs text-sky-700">
                  Gera um link de pagamento único do MP (Checkout Pro) que aceita <strong>PIX, cartão e boleto</strong>.
                  Quando pago, o vencimento é empurrado automaticamente.
                </p>
                <button
                  data-testid="btn-gerar-pix-mensal"
                  onClick={async () => {
                    if (!window.confirm('Gerar cobrança via PIX/cartão/boleto deste mês?\nO link será enviado por e-mail automaticamente.')) return;
                    setLoading(true);
                    try {
                      const res = await tenantsAPI.mpGerarPixMensal(tenant.id, { enviar_email: true });
                      const enviado = res?.data?.email_enviado;
                      showMsg(enviado ? `Cobrança PIX gerada e enviada por e-mail!` : 'Cobrança PIX gerada (não foi possível enviar e-mail).');
                      setTimeout(onRefresh, 1500);
                    } catch (e) {
                      showMsg(e.response?.data?.detail || 'Erro ao gerar PIX', 'error');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-sky-600 text-white rounded-lg text-sm hover:bg-sky-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Gerando...' : 'Gerar Cobrança PIX deste mês'}
                </button>
                {tenant.mp_pix_mensal_init_point && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={tenant.mp_pix_mensal_init_point}
                      className="flex-1 text-xs px-3 py-2 border rounded-lg bg-white"
                      data-testid="pix-link-input"
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(tenant.mp_pix_mensal_init_point); showMsg('Link copiado!'); }}
                      className="px-3 py-2 bg-sky-700 text-white rounded-lg text-xs"
                    >
                      Copiar
                    </button>
                  </div>
                )}
              </div>

              {/* Bloco: Pagamento Manual */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-blue-800">💰 Marcar como Pago Manualmente</h4>
                <p className="text-xs text-blue-700">
                  Use quando o cliente pagou por outra forma (PIX, dinheiro). Empurra o vencimento +1 mês.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    data-testid="select-metodo-pagamento"
                    value={pagManualForm.metodo}
                    onChange={(e) => setPagManualForm(f => ({ ...f, metodo: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="pix">PIX</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="transferencia">Transferência</option>
                    <option value="boleto">Boleto</option>
                    <option value="cartao">Cartão</option>
                    <option value="outro">Outro</option>
                  </select>
                  <input
                    data-testid="input-pagamento-obs"
                    type="text"
                    placeholder="Observação (opcional)"
                    value={pagManualForm.observacao}
                    onChange={(e) => setPagManualForm(f => ({ ...f, observacao: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <button
                  data-testid="btn-pagamento-manual"
                  onClick={handlePagamentoManual}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Registrando...' : 'Registrar Pagamento Manual'}
                </button>
              </div>

              {/* Bloco: Reconciliar Mercado Pago (rede de segurança contra webhook falho) */}
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-amber-900 flex items-center gap-2">
                  🔄 Reconciliar Mercado Pago
                </h4>
                <p className="text-xs text-amber-800">
                  Se o cliente pagou mas o crédito NÃO apareceu automaticamente
                  (webhook do MP falhou), clique aqui. O sistema busca no Mercado Pago
                  os últimos 90 dias de pagamentos e credita os aprovados que ainda
                  não foram lançados.
                </p>
                <button
                  data-testid="btn-mp-reconciliar"
                  onClick={handleReconciliarMP}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Buscando no MP...' : 'Reconciliar agora'}
                </button>
              </div>

              {/* Bloco: Estender Trial */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-indigo-800">🕐 Adicionar Dias ao Trial</h4>
                <p className="text-xs text-indigo-700">
                  Estende o período de teste em casos excepcionais.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    data-testid="input-trial-dias"
                    type="number" min="1" max="365"
                    value={trialForm.dias}
                    onChange={(e) => setTrialForm(f => ({ ...f, dias: e.target.value }))}
                    placeholder="Dias"
                    className="px-3 py-2 border rounded-md text-sm"
                  />
                  <input
                    data-testid="input-trial-motivo"
                    type="text"
                    placeholder="Motivo (obrigatório)"
                    value={trialForm.motivo}
                    onChange={(e) => setTrialForm(f => ({ ...f, motivo: e.target.value }))}
                    className="col-span-2 px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <button
                  data-testid="btn-estender-trial"
                  onClick={handleEstenderTrial}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Estendendo...' : 'Adicionar Dias ao Trial'}
                </button>
              </div>

              {/* Bloco: Histórico */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-gray-800">📜 Histórico de Pagamentos</h4>
                {pagHistorico.pagamentos?.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-3">Nenhum pagamento registrado</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {[...(pagHistorico.pagamentos || [])].reverse().map((p, i) => (
                      <div key={i} className="flex justify-between items-start gap-2 p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.tipo === 'mp' ? 'bg-sky-100 text-sky-700' : 'bg-blue-100 text-blue-700'}`}>
                              {p.tipo === 'mp' ? 'MP' : (p.metodo || '').toUpperCase()}
                            </span>
                            <span className="font-medium">R$ {Number(p.valor).toFixed(2)}</span>
                          </div>
                          {p.observacao && <p className="text-xs text-gray-500 mt-1">{p.observacao}</p>}
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">{p.data?.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {pagHistorico.trial_extensoes?.length > 0 && (
                  <div className="pt-3 border-t">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Extensões de Trial</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {[...(pagHistorico.trial_extensoes || [])].reverse().map((t, i) => (
                        <div key={i} className="flex justify-between items-start gap-2 p-2 bg-indigo-50 rounded-lg text-xs">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">+{t.dias} dias</span>
                            <p className="text-gray-600 mt-0.5 truncate">{t.motivo}</p>
                          </div>
                          <span className="text-gray-500 whitespace-nowrap">{t.data?.slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB: Historico (Log) */}
          {tab === 'historico' && (
            <div className="space-y-2">
              {historico.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">Nenhum evento registrado</p>
              ) : (
                historico.map(h => (
                  <div key={h.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="w-2 h-2 mt-1.5 rounded-full bg-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800">{formatEventName(h.evento)}</p>
                      {h.detalhes && Object.keys(h.detalhes).length > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {Object.entries(h.detalhes).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">{formatDate(h.created_at)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatEventName = (evento) => {
  const names = {
    tenant_criado: 'Tenant Criado',
    assinatura_ativada_manual: 'Assinatura Ativada (Manual)',
    assinatura_suspensa_manual: 'Assinatura Suspensa (Manual)',
    assinatura_vitalicia: 'Assinatura Vitalicia',
    periodo_estendido: 'Periodo Estendido',
    assinatura_cancelada_manual: 'Assinatura Cancelada (Manual)',
    mp_assinatura_criada: 'Assinatura MP Criada',
    mp_assinatura_cancelada: 'Assinatura MP Cancelada',
    mp_webhook_updated: 'Status Atualizado via MP',
    mp_pagamento_aprovado: 'Pagamento Aprovado (MP)',
    mp_pagamento_rejeitado: 'Pagamento Rejeitado (MP)',
  };
  return names[evento] || evento;
};

// ========== INFO ROW ==========
const InfoRow = ({ label, value, small }) => (
  <div className={small ? '' : 'bg-gray-50 rounded-lg px-3 py-2'}>
    <p className={`text-gray-500 ${small ? 'text-xs' : 'text-xs'}`}>{label}</p>
    <p className={`font-medium text-gray-900 ${small ? 'text-xs' : 'text-sm'} truncate`}>{value}</p>
  </div>
);

// ========== ACTION BUTTON ==========
const ActionButton = ({ label, color, onClick, disabled, ...props }) => {
  const colors = {
    green: 'bg-green-100 text-green-700 hover:bg-green-200',
    emerald: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
    blue: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
    orange: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
    red: 'bg-red-100 text-red-700 hover:bg-red-200',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${colors[color] || colors.green}`}
      {...props}
    >
      {label}
    </button>
  );
};

// ========== NEW TENANT MODAL ==========
const NewTenantModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    slug: '',
    db_name: '',
    dominio: '',
    email_financeiro: '',
    trial_days: 30,
    subscription_plan: 'monthly',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'nome' && !prev.slug) {
        const slug = value.toLowerCase().replace(/[^a-z0-9]/g, '');
        newData.slug = slug;
        newData.db_name = slug;
        newData.dominio = `${slug}.agrolinkbr.com.br`;
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await tenantsAPI.create(formData);
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Novo Cliente</h2>
          <p className="text-sm text-gray-500">Cadastre um novo cliente no sistema</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa</label>
            <input type="text" name="nome" value={formData.nome} onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" name="slug" value={formData.slug} onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Banco de Dados</label>
              <input type="text" name="db_name" value={formData.db_name} onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dominio</label>
            <input type="text" name="dominio" value={formData.dominio} onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail Financeiro</label>
            <input type="email" name="email_financeiro" value={formData.email_financeiro} onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dias de Trial</label>
              <input type="number" name="trial_days" value={formData.trial_days} onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" min="1" max="365" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plano Inicial</label>
              <select name="subscription_plan" value={formData.subscription_plan} onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                <option value="monthly">Mensal</option>
                <option value="yearly">Anual</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
              {loading ? 'Criando...' : 'Criar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
